import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Upload, FileText, History, Loader2, CheckCircle2, AlertTriangle, Copy, Trash2, FileStack } from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_COLORS = {
  create: "bg-emerald-100 text-emerald-700 border-emerald-300",
  duplicate: "bg-slate-100 text-slate-600 border-slate-300",
  discrepancy: "bg-amber-100 text-amber-800 border-amber-400",
  update: "bg-blue-100 text-blue-700 border-blue-300",
  skip: "bg-rose-100 text-rose-700 border-rose-300",
};

const ACTION_ICONS = {
  create: <CheckCircle2 className="h-3 w-3" />,
  duplicate: <Copy className="h-3 w-3" />,
  discrepancy: <AlertTriangle className="h-3 w-3" />,
};

export default function UploadAndExtract() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("upload");
  const [busy, setBusy] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [history, setHistory] = useState([]);
  const [decisions, setDecisions] = useState({ clients: {}, employees: {} });
  const fileRef = useRef(null);

  const loadHistory = async () => {
    try {
      const res = await axios.get(`${API}/upload-extract/history`);
      setHistory(res.data.uploads || []);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/upload-extract/history`);
        if (!cancelled) {
          setHistory(res.data.uploads || []);
        }
      } catch (e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const onPickFile = (file) => {
    runAnalysis(file);
  };

  async function runAnalysis(file) {
    if (!file) {
      return;
    }
    setBusy(true);
    setAnalysis(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/upload-extract/analyze`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAnalysis(res.data);
      // Initialize decisions with the AI's proposed action (default skip for duplicates)
      const ex = res.data.extracted || {};
      const initial = { clients: {}, employees: {} };
      (ex.clients || []).forEach((c, idx) => {
        initial.clients[idx] = c.proposed_action === "duplicate" ? "skip" : (c.proposed_action === "discrepancy" ? "update" : "create");
      });
      (ex.employees || []).forEach((e, idx) => {
        initial.employees[idx] = e.proposed_action === "duplicate" ? "skip" : (e.proposed_action === "discrepancy" ? "update" : "create");
      });
      setDecisions(initial);
      toast.success("Analyzed");
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analysis failed");
    } finally {
      setBusy(false);
    }
  }

  const setAction = (kind, idx, action) => {
    const nextSection = { ...decisions[kind], [idx]: action };
    const nextDecisions = { ...decisions, [kind]: nextSection };
    setDecisions(nextDecisions);
  };

  const commit = async () => {
    if (!analysis) return;
    setBusy(true);
    try {
      const ex = analysis.extracted || {};
      const payload = {
        analysis_id: analysis.analysis_id,
        decisions: {
          clients: (ex.clients || []).map((c, idx) => ({
            action: decisions.clients[idx] || "skip",
            existing_id: c.match?.existing_id,
            name: c.name, email: c.email, phone: c.phone, address: c.address,
          })),
          employees: (ex.employees || []).map((e, idx) => ({
            action: decisions.employees[idx] || "skip",
            existing_id: e.match?.existing_id,
            name: e.name, email: e.email, phone: e.phone, role: e.role,
          })),
        },
      };
      const res = await axios.post(`${API}/upload-extract/commit`, payload);
      toast.success(`Imported ${res.data.created.clients} clients, ${res.data.created.employees} employees`);
      setAnalysis(null);
      loadHistory();
      setTab("history");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Commit failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this analysis from history?")) return;
    try {
      await axios.delete(`${API}/upload-extract/${id}`);
      loadHistory();
      toast.success("Deleted");
    } catch (e) { toast.error("Failed to delete"); }
  };

  const saveAsTemplate = async () => {
    if (!analysis) return;
    const docType = (ex?.document_type || "quote");
    const kind = ["quote", "invoice", "job_sheet", "safety_sheet", "training_sheet", "payroll", "email_reply", "text_reply", "portal_msg_reply", "custom"].includes(docType) ? docType : "custom";
    const name = window.prompt("Template name", `From ${analysis.file_name}`);
    if (!name) return;
    try {
      const res = await axios.post(`${API}/upload-extract/${analysis.analysis_id}/save-as-template`, { name, kind });
      toast.success(`Template "${res.data.name}" created`);
      navigate("/universal-templates");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save template");
    }
  };

  const ex = analysis?.extracted || null;
  const summary = ex?.summary || {};

  return (
    <div className="p-6 space-y-6" data-testid="upload-extract-page">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2"><Upload className="h-7 w-7 text-emerald-600" /> Upload & Extract</h1>
        <p className="text-muted-foreground mt-1">Drop in a PDF, Word doc, or Excel sheet. We&apos;ll extract clients, employees, and line items, then flag any duplicates or discrepancies before you import them.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="upload" data-testid="ue-tab-upload"><Upload className="h-4 w-4 mr-1" /> New Upload</TabsTrigger>
          <TabsTrigger value="history" data-testid="ue-tab-history"><History className="h-4 w-4 mr-1" /> History ({history.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="upload" className="space-y-4">
          {!analysis ? (
            <Card>
              <CardContent className="p-12">
                <div
                  className="border-2 border-dashed rounded-lg p-12 text-center cursor-pointer hover:border-emerald-400 transition"
                  onClick={() => fileRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); }}
                  onDrop={(e) => { e.preventDefault(); onPickFile(e.dataTransfer.files?.[0]); }}
                  data-testid="upload-dropzone"
                >
                  {busy ? (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="h-10 w-10 animate-spin text-emerald-600" />
                      <p className="text-sm text-muted-foreground">Analyzing… (Claude is extracting structure)</p>
                    </div>
                  ) : (
                    <>
                      <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                      <p className="font-medium">Drop a file here or click to browse</p>
                      <p className="text-xs text-muted-foreground mt-1">Supported: PDF · DOCX · XLSX · CSV · TXT  (max 15 MB)</p>
                    </>
                  )}
                  <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.xlsx,.xlsm,.xls,.csv,.txt,.md" className="hidden" onChange={(e) => onPickFile(e.target.files?.[0])} data-testid="upload-file-input" />
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <CardTitle className="text-base">{analysis.file_name} <span className="text-xs text-muted-foreground ml-2">({analysis.file_kind})</span></CardTitle>
                    <div className="flex gap-2 flex-wrap text-xs">
                      <Badge variant="outline">📄 {ex.document_type || "other"}</Badge>
                      <Badge variant="outline">{summary.clients_new || 0} new clients</Badge>
                      <Badge variant="outline">{summary.clients_dup || 0} dup clients</Badge>
                      <Badge variant="outline">{summary.clients_discrep || 0} discrep</Badge>
                      <Badge variant="outline">{summary.employees_new || 0} new employees</Badge>
                    </div>
                  </div>
                </CardHeader>
              </Card>

              {(ex.clients?.length > 0) && (
                <Card>
                  <CardHeader><CardTitle className="text-base">Clients ({ex.clients.length})</CardTitle></CardHeader>
                  <CardContent className="space-y-2" data-testid="extracted-clients-list">
                    {ex.clients.map((c, idx) => (
                      <div key={idx} className="p-3 border rounded-md space-y-2">
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                          <div>
                            <p className="font-medium text-sm">{c.name || "—"} <Badge variant="outline" className={`ml-1 text-xs ${ACTION_COLORS[c.proposed_action] || ""}`}>{ACTION_ICONS[c.proposed_action]}{c.proposed_action}</Badge></p>
                            <p className="text-xs text-muted-foreground">{c.email || "no email"} · {c.phone || "no phone"}</p>
                            {c.address && <p className="text-xs text-muted-foreground">{c.address}</p>}
                            {c.match && (<p className="text-xs text-blue-600">matches existing: <strong>{c.match.name}</strong> ({Math.round(c.match.similarity * 100)}% similar)</p>)}
                            {c.fields_diff?.length > 0 && (
                              <div className="mt-1 text-xs">
                                {c.fields_diff.map((d, i) => (
                                  <p key={i} className="text-amber-800">⚠ {d.field}: existing &quot;{d.existing}&quot; vs incoming &quot;{d.incoming}&quot;</p>
                                ))}
                              </div>
                            )}
                          </div>
                          <select className="border rounded-md px-2 py-1 text-xs" value={decisions.clients[idx] || "skip"} onChange={(e) => setAction("clients", idx, e.target.value)} data-testid={`client-action-${idx}`}>
                            <option value="create">Create new</option>
                            {c.match && <option value="update">Update existing</option>}
                            <option value="skip">Skip</option>
                          </select>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {(ex.employees?.length > 0) && (
                <Card>
                  <CardHeader><CardTitle className="text-base">Employees ({ex.employees.length})</CardTitle></CardHeader>
                  <CardContent className="space-y-2" data-testid="extracted-employees-list">
                    {ex.employees.map((e, idx) => (
                      <div key={idx} className="p-3 border rounded-md">
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                          <div>
                            <p className="font-medium text-sm">{e.name || "—"} <Badge variant="outline" className={`ml-1 text-xs ${ACTION_COLORS[e.proposed_action] || ""}`}>{ACTION_ICONS[e.proposed_action]}{e.proposed_action}</Badge></p>
                            <p className="text-xs text-muted-foreground">{e.email || "no email"} · {e.phone || "no phone"} · {e.role || "no role"}</p>
                            {e.match && (<p className="text-xs text-blue-600">matches existing: <strong>{e.match.name}</strong></p>)}
                          </div>
                          <select className="border rounded-md px-2 py-1 text-xs" value={decisions.employees[idx] || "skip"} onChange={(ev) => setAction("employees", idx, ev.target.value)} data-testid={`employee-action-${idx}`}>
                            <option value="create">Create new</option>
                            {e.match && <option value="update">Update existing</option>}
                            <option value="skip">Skip</option>
                          </select>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {analysis.raw_text_preview && (
                <Card>
                  <CardHeader><CardTitle className="text-base">Raw Text Preview</CardTitle></CardHeader>
                  <CardContent>
                    <pre className="text-xs whitespace-pre-wrap bg-muted/30 p-3 rounded-md max-h-48 overflow-y-auto">{analysis.raw_text_preview}</pre>
                  </CardContent>
                </Card>
              )}

              <div className="flex gap-2 sticky bottom-4 bg-background p-3 border rounded-md shadow-md">
                <Button variant="outline" onClick={() => setAnalysis(null)}>Discard</Button>
                <Button variant="outline" onClick={saveAsTemplate} data-testid="save-as-template-btn">
                  <FileStack className="h-4 w-4 mr-1" /> Save as Template
                </Button>
                <Button onClick={commit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 flex-1" data-testid="commit-import-btn">
                  {busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                  Import selected records
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="history" className="space-y-2">
          {history.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-muted-foreground" data-testid="history-empty">No uploads yet.</CardContent></Card>
          ) : history.map((u) => (
            <Card key={u.id} data-testid={`history-card-${u.id}`}>
              <CardContent className="p-3 flex items-center justify-between gap-2">
                <div>
                  <p className="font-medium text-sm">{u.file_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {u.file_kind} · {new Date(u.created_at).toLocaleString()} ·
                    <Badge variant="outline" className="ml-2">{u.status}</Badge>
                    {u.summary?.clients_total > 0 && <span className="ml-2">{u.summary.clients_total} clients</span>}
                    {u.summary?.employees_total > 0 && <span className="ml-2">{u.summary.employees_total} employees</span>}
                  </p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => remove(u.id)} className="text-rose-600" data-testid={`delete-upload-${u.id}`}><Trash2 className="h-4 w-4" /></Button>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
